import { useEffect, useMemo, useRef, useState } from "react";

const IapImageCropperModal = ({
  isOpen,
  imageSrc,
  onCancel,
  onConfirm,
  title = "Crop Image",
  targetWidth = 1260,
  targetHeight = 2736,
  useSourceDimensions = false,
}) => {
  const viewportRef = useRef(null);

  const [imageEl, setImageEl] = useState(null);
  const [scale, setScale] = useState(1);
  const [minScale, setMinScale] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [viewportBounds, setViewportBounds] = useState({
    width: typeof window === "undefined" ? 1280 : window.innerWidth,
    height: typeof window === "undefined" ? 720 : window.innerHeight,
  });

  const aspectRatio = targetWidth / targetHeight;

  const previewSize = useMemo(() => {
    const maxModalWidth = Math.max(280, viewportBounds.width - 120);
    const maxFrameWidth = Math.min(820, maxModalWidth - 40);
    const maxFrameHeight = Math.min(560, Math.max(220, viewportBounds.height - 320));

    let width = Math.min(maxFrameWidth, Math.round(maxFrameHeight * aspectRatio));
    let height = Math.round(width / aspectRatio);

    if (height > maxFrameHeight) {
      height = maxFrameHeight;
      width = Math.round(height * aspectRatio);
    }

    return {
      width: Math.max(220, width),
      height: Math.max(180, height),
    };
  }, [aspectRatio, viewportBounds.height, viewportBounds.width]);

  const scaledImageSize = useMemo(() => {
    if (!imageEl) {
      return { width: 0, height: 0 };
    }

    return {
      width: Math.round(imageEl.naturalWidth * scale),
      height: Math.round(imageEl.naturalHeight * scale),
    };
  }, [imageEl, scale]);

  useEffect(() => {
    if (!isOpen || !imageSrc) {
      setImageEl(null);
      setScale(1);
      setMinScale(1);
      return;
    }

    const img = new Image();
    img.onload = () => {
      const fitScale = Math.max(
        previewSize.width / img.naturalWidth,
        previewSize.height / img.naturalHeight,
      );
      setImageEl(img);
      setMinScale(fitScale);
      setScale(fitScale);
    };
    img.src = imageSrc;
  }, [imageSrc, isOpen, previewSize.height, previewSize.width]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const syncViewportBounds = () => {
      setViewportBounds({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    syncViewportBounds();
    window.addEventListener("resize", syncViewportBounds);
    return () => window.removeEventListener("resize", syncViewportBounds);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !viewportRef.current || !scaledImageSize.width || !scaledImageSize.height) {
      return;
    }

    const viewport = viewportRef.current;
    const left = Math.max(0, (scaledImageSize.width - previewSize.width) / 2);
    const top = Math.max(0, (scaledImageSize.height - previewSize.height) / 2);

    viewport.scrollLeft = left;
    viewport.scrollTop = top;
  }, [
    isOpen,
    imageSrc,
    previewSize.height,
    previewSize.width,
    scaledImageSize.height,
    scaledImageSize.width,
  ]);

  const handleZoomChange = (event) => {
    const nextScale = Number(event.target.value);
    const viewport = viewportRef.current;

    if (!viewport || !scaledImageSize.width || !scaledImageSize.height) {
      setScale(nextScale);
      return;
    }

    const centerX = viewport.scrollLeft + viewport.clientWidth / 2;
    const centerY = viewport.scrollTop + viewport.clientHeight / 2;
    const ratioX = centerX / scaledImageSize.width;
    const ratioY = centerY / scaledImageSize.height;

    setScale(nextScale);

    requestAnimationFrame(() => {
      const nextWidth = imageEl?.naturalWidth ? imageEl.naturalWidth * nextScale : 0;
      const nextHeight = imageEl?.naturalHeight ? imageEl.naturalHeight * nextScale : 0;

      viewport.scrollLeft = Math.max(0, ratioX * nextWidth - viewport.clientWidth / 2);
      viewport.scrollTop = Math.max(0, ratioY * nextHeight - viewport.clientHeight / 2);
    });
  };

  const exportCroppedBlob = async () => {
    if (!imageEl || !viewportRef.current) return;

    setIsSaving(true);
    try {
      const viewport = viewportRef.current;
      const canvas = document.createElement("canvas");
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas not supported");

      const sourceX = viewport.scrollLeft / scale;
      const sourceY = viewport.scrollTop / scale;
      const sourceWidth = previewSize.width / scale;
      const sourceHeight = previewSize.height / scale;
      const outputWidth = useSourceDimensions
        ? Math.max(1, Math.round(sourceWidth))
        : targetWidth;
      const outputHeight = useSourceDimensions
        ? Math.max(1, Math.round(sourceHeight))
        : targetHeight;

      canvas.width = outputWidth;
      canvas.height = outputHeight;

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.clearRect(0, 0, outputWidth, outputHeight);
      ctx.drawImage(
        imageEl,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        outputWidth,
        outputHeight,
      );

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((result) => {
          if (result) resolve(result);
          else reject(new Error("Failed to generate cropped image"));
        }, "image/jpeg", 0.92);
      });

      await onConfirm(blob);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        zIndex: 1055,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        cursor: "default",
      }}
    >
      <div
        style={{
          width: "min(860px, 100%)",
          maxHeight: "90vh",
          overflowY: "auto",
          overflowX: "hidden",
          backgroundColor: "#fff",
          borderRadius: "16px",
          padding: "20px",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.25)",
          cursor: "default",
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="mb-1">{title}</h5>
            <div className="text-muted small">
              {useSourceDimensions
                ? `Crop aspect ratio: ${targetWidth} : ${targetHeight}`
                : `Fixed crop area: ${targetWidth} x ${targetHeight}`}
            </div>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={onCancel}
            disabled={isSaving}
          >
            Close
          </button>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "20px",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${previewSize.width}px`,
              maxWidth: "100%",
            }}
          >
            <div
              style={{
                border: "2px solid rgba(15, 23, 42, 0.15)",
                borderRadius: "16px",
                overflow: "hidden",
                background:
                  "linear-gradient(135deg, rgba(226, 232, 240, 0.85), rgba(203, 213, 225, 0.9))",
              }}
            >
              <div
                ref={viewportRef}
                style={{
                  width: `${previewSize.width}px`,
                  height: `${previewSize.height}px`,
                  overflow: "auto",
                  scrollbarWidth: "thin",
                  cursor: imageEl && !isSaving ? "grab" : "default",
                }}
              >
                {imageEl ? (
                  <img
                    src={imageSrc}
                    alt="Crop source"
                    draggable={false}
                    style={{
                      display: "block",
                      width: `${scaledImageSize.width}px`,
                      height: `${scaledImageSize.height}px`,
                      maxWidth: "none",
                      userSelect: "none",
                      cursor: isSaving ? "default" : "grab",
                    }}
                  />
                ) : null}
              </div>
            </div>

            <div className="text-muted small mt-2">
              Scroll inside the frame to choose the visible crop area.
            </div>
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label fs-7">Zoom</label>
          <input
            type="range"
            className="form-range"
            min={minScale}
            max={Math.max(minScale * 4, minScale + 1)}
            step="0.01"
            value={scale}
            onChange={handleZoomChange}
            disabled={!imageEl || isSaving}
          />
          <div className="text-muted small">
            The image stays fixed. Use zoom to enlarge it, then scroll the crop area.
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onCancel}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={exportCroppedBlob}
            disabled={!imageEl || isSaving}
          >
            {isSaving ? "Saving..." : "Crop & Upload"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IapImageCropperModal;
